/**
 * Feedback Service
 * Centralizes all feedback and bug reporting API operations
 */

import apiService from './api';

/**
 * Submit general feedback
 * @param {Object} feedbackData - Feedback data
 * @param {string} feedbackData.type - Feedback type (e.g., 'feedback', 'suggestion', 'praise')
 * @param {string} feedbackData.subject - Feedback subject
 * @param {string} feedbackData.message - Feedback message
 * @param {string} [feedbackData.email] - Contact email (optional, uses user email if authenticated)
 * @returns {Promise<Object>} Submission result
 */
const submitFeedback = async (feedbackData) => {
  const response = await apiService.post('/api/feedback/submit', {
    type: feedbackData.type,
    subject: feedbackData.subject,
    message: feedbackData.message,
    email: feedbackData.email || null
  });
  return response.json();
};

/**
 * Submit a bug report
 * @param {Object} bugReportData - Bug report data
 * @param {string} bugReportData.page - Page where bug occurred
 * @param {string} bugReportData.whatWentWrong - Description of what went wrong
 * @param {string} [bugReportData.howToReproduce] - Steps to reproduce the bug
 * @param {string} [bugReportData.email] - Contact email (optional, uses user email if authenticated)
 * @returns {Promise<Object>} Submission result
 */
const submitBugReport = async (bugReportData) => {
  const response = await apiService.post('/api/feedback/submit', {
    type: 'bug',
    subject: `Bug Report: ${bugReportData.page}`,
    message: bugReportData.whatWentWrong,
    pageSection: bugReportData.page,
    reproductionSteps: bugReportData.howToReproduce || null,
    email: bugReportData.email || null
  });
  return response.json();
};

/**
 * Submit feature request
 * @param {Object} featureData - Feature request data
 * @param {string} featureData.title - Feature title
 * @param {string} featureData.description - Feature description
 * @param {string} featureData.reasoning - Why this feature would be useful
 * @param {string} [featureData.email] - Contact email (optional)
 * @returns {Promise<Object>} Submission result
 */
const submitFeatureRequest = async (featureData) => {
  const response = await apiService.post('/api/feedback/submit', {
    type: 'feature-request',
    subject: `Feature Request: ${featureData.title}`,
    message: featureData.description,
    reasoning: featureData.reasoning,
    email: featureData.email || null
  });
  return response.json();
};

/**
 * Get public feedback statistics (if available)
 * @returns {Promise<Object>} Public feedback stats
 */
const getFeedbackStats = async () => {
  const response = await apiService.get('/api/feedback/stats');
  return response.json();
};

/**
 * Get user's own submitted feedback (if authenticated)
 * @param {Object} [params] - Query parameters
 * @param {number} [params.page] - Page number
 * @param {number} [params.limit] - Items per page
 * @param {string} [params.type] - Filter by feedback type
 * @param {string} [params.status] - Filter by status
 * @returns {Promise<Object>} User's feedback with pagination
 */
const getMyFeedback = async (params = {}) => {
  const searchParams = new URLSearchParams(
    Object.entries(params).filter(([_, value]) => value !== '' && value != null)
  );
  const response = await apiService.get(`/api/feedback/my?${searchParams}`);
  return response.json();
};

/**
 * Update user's own feedback (if allowed)
 * @param {string} feedbackId - Feedback ID
 * @param {Object} updates - Fields to update
 * @param {string} [updates.message] - Updated message
 * @param {string} [updates.subject] - Updated subject
 * @returns {Promise<Object>} Updated feedback
 */
const updateMyFeedback = async (feedbackId, updates) => {
  const response = await apiService.put(`/api/feedback/my/${feedbackId}`, updates);
  return response.json();
};

/**
 * Delete user's own feedback (if allowed)
 * @param {string} feedbackId - Feedback ID
 * @returns {Promise<Object>} Delete result
 */
const deleteMyFeedback = async (feedbackId) => {
  const response = await apiService.delete(`/api/feedback/my/${feedbackId}`);
  return response.json();
};

/**
 * Submit data correction report
 * @param {Object} correctionData - Data correction report
 * @param {string} correctionData.gameName - Name of the game
 * @param {string} correctionData.itemName - Name of the item with incorrect data
 * @param {string[]} correctionData.incorrectCategories - Array of category keys with incorrect data
 * @param {string} [correctionData.additionalNotes] - Additional information about the correction
 * @returns {Promise<Object>} Submission result
 */
const submitDataCorrection = async (correctionData) => {
  const categoriesText = correctionData.incorrectCategories.join(', ');
  const message = `Item: ${correctionData.itemName}\nIncorrect categories: ${categoriesText}${correctionData.additionalNotes ? `\n\nAdditional notes: ${correctionData.additionalNotes}` : ''}`;
  
  const response = await apiService.post('/api/feedback/submit', {
    type: 'feedback',
    subject: `Data Correction: ${correctionData.gameName} - ${correctionData.itemName}`,
    message: message,
    pageSection: correctionData.gameName
  });
  return response.json();
};

export default {
  submitFeedback,
  submitBugReport,
  submitFeatureRequest,
  submitDataCorrection,
  getFeedbackStats,
  getMyFeedback,
  updateMyFeedback,
  deleteMyFeedback
};